/**
 * Sitio demo público (otra URL, tipo Odoo).
 *
 * Hay DOS maneras de estar en modo demostración, y no son lo mismo:
 *
 *  - `host`    → la dirección con la que se abrió la app ES la del demo
 *                (VITE_DEMO_PUBLIC_HOSTS, p.ej. demo.ai.opus.com.mx) o el build
 *                entero es un deploy dedicado al demo (VITE_DEMO_PUBLIC=1).
 *                Es el sitio público de verdad: no se puede apagar desde el
 *                navegador ni ofrece salida, porque ahí no hay a dónde salir.
 *  - `session` → alguien lo prendió A MANO en una pestaña con `?demo=1`, para
 *                mirar cómo se ve. Vive en sessionStorage (muere al cerrar la
 *                pestaña), se apaga con `?demo=0` y la pantalla ofrece salida.
 *
 * La distinción existe porque el modo "a mano" se quedaba pegado: bastaba abrir
 * una vez `localhost:8080/?demo=1` para que el entorno de desarrollo quedara
 * atrapado en el demo, sin login y sin puerta de vuelta.
 *
 * Distinto del mockup estático VITE_DEMO (build:demo / fixtures).
 */

const SS_KEY = 'opus_public_demo';

/** De dónde sale el modo demostración (o `off` si no está activo). */
export type DemoActivation = 'off' | 'host' | 'session';

/** Qué hacer con la marca de la pestaña después de leer la URL. */
type SessionAction = 'keep' | 'set' | 'clear';

function parseHosts(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
}

/** ¿La dirección actual es una de las del sitio demo? */
export function demoHostMatches(
  hostsRaw: string | undefined,
  host: string,
  hostname: string,
): boolean {
  const allowed = parseHosts(hostsRaw);
  if (allowed.length === 0) return false;
  const full = host.toLowerCase();
  const bare = hostname.toLowerCase();
  return allowed.some((a) => a === full || a === bare || a.split(':')[0] === bare);
}

/** `?demo=1` prende, `?demo=0` apaga. Cualquier otra cosa no opina. */
export function readDemoParam(search: string): 'on' | 'off' | null {
  let value: string | null = null;
  try {
    value = new URLSearchParams(search).get('demo');
  } catch {
    return null;
  }
  if (value === null) return null;
  const v = value.trim().toLowerCase();
  if (v === '1' || v === 'true' || v === 'on' || v === 'si') return 'on';
  if (v === '0' || v === 'false' || v === 'off' || v === 'no') return 'off';
  return null;
}

/**
 * Decisión pura (sin `window`) de en qué modo está la pestaña.
 *
 * El host manda: en el sitio demo de verdad, `?demo=0` limpia la marca de la
 * pestaña pero NO apaga el demo — producción se comporta exactamente igual que
 * antes de este cambio.
 */
export function resolveDemoActivation(input: {
  search: string;
  sessionFlag: string | null;
  buildFlag: string | undefined;
  hostsRaw: string | undefined;
  host: string;
  hostname: string;
}): { mode: DemoActivation; session: SessionAction } {
  const byHost =
    input.buildFlag === '1' ||
    input.buildFlag === 'true' ||
    demoHostMatches(input.hostsRaw, input.host, input.hostname);

  const param = readDemoParam(input.search);
  if (param === 'off') {
    return { mode: byHost ? 'host' : 'off', session: 'clear' };
  }
  if (param === 'on') {
    return { mode: byHost ? 'host' : 'session', session: 'set' };
  }
  if (byHost) return { mode: 'host', session: 'keep' };
  return { mode: input.sessionFlag === '1' ? 'session' : 'off', session: 'keep' };
}

/** Lee la situación real de la pestaña y persiste lo que pidió la URL. */
export function publicDemoActivation(): DemoActivation {
  if (typeof window === 'undefined') return 'off';

  let sessionFlag: string | null = null;
  try {
    sessionFlag = sessionStorage.getItem(SS_KEY);
  } catch {
    /* modo privado */
  }

  const { mode, session } = resolveDemoActivation({
    search: window.location.search,
    sessionFlag,
    buildFlag: import.meta.env.VITE_DEMO_PUBLIC as string | undefined,
    hostsRaw: import.meta.env.VITE_DEMO_PUBLIC_HOSTS as string | undefined,
    host: window.location.host,
    hostname: window.location.hostname,
  });

  try {
    if (session === 'set') sessionStorage.setItem(SS_KEY, '1');
    else if (session === 'clear') sessionStorage.removeItem(SS_KEY);
  } catch {
    /* modo privado */
  }

  return mode;
}

/** ¿Esta pestaña está en modo demostración, venga de donde venga? */
export function isPublicDemoHost(): boolean {
  return publicDemoActivation() !== 'off';
}

/** Apaga el modo demostración de esta pestaña. */
export function exitPublicDemo(): void {
  try {
    sessionStorage.removeItem(SS_KEY);
  } catch {
    /* modo privado */
  }
}

/**
 * Apaga el demo y recarga la app en el inicio de sesión normal. Recarga a
 * propósito: así se tira cualquier estado del recorrido de demostración.
 */
export function exitPublicDemoAndReload(): void {
  exitPublicDemo();
  if (typeof window !== 'undefined') window.location.replace('/login');
}

/** CTA fuera del demo. Default hub OPUS (nunca la URL del propio demo). */
export function publicDemoProductUrl(): string {
  const fromEnv = (import.meta.env.VITE_DEMO_PUBLIC_PRODUCT_URL as string | undefined)?.trim();
  if (fromEnv) return fromEnv;
  return 'https://apps.opus.com.mx';
}
